// src/components/VehicleLog/VehicleCard.js
import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Chip,
  Button,
  IconButton,
  Collapse,
  ImageList,
  ImageListItem,
  Divider,
  Avatar,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  DialogContentText
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  DirectionsCar as DirectionsCarIcon,
  Person as PersonIcon,
  QrCode as QrCodeIcon,
  ExitToApp as ExitToAppIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  PhotoCamera as PhotoCameraIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon
} from '@mui/icons-material';

const VehicleCard = ({ vehicle, onRegisterExit, onDelete }) => {
  const [showImages, setShowImages] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return 'No registrado';
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isActive = !vehicle.fechaSalida;

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    onDelete(vehicle.id);
    setDeleteDialogOpen(false);
  };

  const handleImageClick = (image) => {
    setSelectedImage(image);
  };

  return (
    <>
      <Card 
        elevation={2}
        sx={{ 
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderLeft: `4px solid ${isActive ? '#10b981' : '#6b7280'}`,
          transition: 'all 0.3s ease',
          '&:hover': {
            elevation: 8,
            transform: 'translateY(-4px)',
            boxShadow: '0 12px 40px rgba(220, 38, 38, 0.15)'
          }
        }}
      >
        {/* Header */}
        <Box 
          sx={{ 
            p: 2, 
            pb: 1, 
            background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
            color: 'white'
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <QrCodeIcon sx={{ color: 'white' }} />
              <Typography 
                variant="body2" 
                fontFamily="monospace" 
                fontWeight="bold"
                color="white"
              >
                {vehicle.qrNumber}
              </Typography>
            </Box>
            
            <Chip
              icon={isActive ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
              label={isActive ? 'En instalaciones' : 'Salió'}
              color={isActive ? 'success' : 'default'}
              size="small"
              variant="filled"
              sx={{ 
                bgcolor: isActive ? 'rgba(16, 185, 129, 0.9)' : 'rgba(255,255,255,0.2)',
                color: 'white',
                fontWeight: 'bold'
              }}
            />
          </Box>
        </Box>

        <CardContent sx={{ flexGrow: 1, p: 2 }}>
          {/* Información Personal */}
          <Box display="flex" alignItems="center" gap={1.5} mb={2}>
            <Avatar sx={{ bgcolor: 'primary.main', color: 'white' }}>
              <PersonIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" component="h3" fontWeight="bold" color="primary.main">
                {vehicle.nombre} {vehicle.apellido}
              </Typography>
            </Box>
          </Box>

          {/* Información del Vehículo */}
          <Box display="flex" alignItems="center" gap={1.5} mb={2}>
            <DirectionsCarIcon sx={{ color: 'primary.main' }} />
            <Box>
              <Typography variant="body1" fontWeight="bold" color="primary.dark">
                {vehicle.placa}
              </Typography>
              {vehicle.color && (
                <Typography variant="body2" color="text.secondary">
                  {vehicle.color}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Tiempos */}
          <Box mb={2}>
            <Box display="flex" alignItems="flex-start" gap={1.5} mb={1}>
              <ScheduleIcon sx={{ color: 'primary.main', mt: 0.2 }} />
              <Box flexGrow={1}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <Typography variant="body2" color="primary.main" fontWeight="bold">
                    Entrada:
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {formatDate(vehicle.fechaEntrada)}
                  </Typography>
                </Box>
                
                {vehicle.fechaSalida && (
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="primary.main" fontWeight="bold">
                      Salida:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formatDate(vehicle.fechaSalida)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          {/* Observaciones */}
          {vehicle.observaciones && (
            <Box mb={2}>
              <Typography variant="body2" color="primary.main" fontWeight="bold" gutterBottom>
                Observaciones:
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  bgcolor: 'grey.50', 
                  p: 1.5, 
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'grey.200',
                  borderLeft: '3px solid',
                  borderLeftColor: 'primary.main'
                }}
              >
                {vehicle.observaciones}
              </Typography>
            </Box>
          )}

          {/* Sección de Imágenes */}
          {vehicle.imagenes && vehicle.imagenes.length > 0 && (
            <Box>
              <Divider sx={{ my: 2 }} />
              
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  <PhotoCameraIcon sx={{ color: 'primary.main' }} />
                  <Typography variant="body2" color="primary.main" fontWeight="bold">
                    {vehicle.imagenes.length} imagen(es)
                  </Typography>
                </Box>
                
                <IconButton
                  size="small"
                  onClick={() => setShowImages(!showImages)}
                  sx={{ color: 'primary.main' }}
                >
                  {showImages ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </Box>
              
              <Collapse in={showImages}>
                <ImageList cols={2} rowHeight={80} gap={4}>
                  {vehicle.imagenes.map((img) => (
                    <ImageListItem 
                      key={img.id}
                      sx={{ 
                        cursor: 'pointer',
                        borderRadius: 1,
                        overflow: 'hidden',
                        '&:hover': { opacity: 0.8 }
                      }}
                      onClick={() => handleImageClick(img)}
                    >
                      <img
                        src={img.url}
                        alt={img.name}
                        style={{
                          width: '100%',
                          height: 80,
                          objectFit: 'cover'
                        }}
                      />
                    </ImageListItem>
                  ))}
                </ImageList>
              </Collapse>
            </Box>
          )}
        </CardContent>

        {/* Acciones */}
        <CardActions sx={{ p: 2, pt: 0, gap: 1 }}>
          {isActive && (
            <Button
              variant="contained"
              color="success"
              startIcon={<ExitToAppIcon />}
              onClick={() => onRegisterExit(vehicle.id)}
              fullWidth
              size="small"
            >
              Registrar Salida
            </Button>
          )}
          
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteClick}
            size="small"
            sx={{ minWidth: 100 }}
          >
            Eliminar
          </Button>
        </CardActions>
      </Card>

      {/* Dialog de confirmación de eliminación */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro de que deseas eliminar el registro de {vehicle.nombre} {vehicle.apellido} ({vehicle.placa})?
            Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancelar
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para mostrar imagen ampliada */}
      <Dialog
        open={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ p: 0 }}>
          {selectedImage && (
            <img
              src={selectedImage.url}
              alt={selectedImage.name}
              style={{
                width: '100%',
                height: 'auto',
                display: 'block'
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedImage(null)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VehicleCard;